import React, { useState, useEffect, useRef } from 'react';
import { Modal, Typography, Button, Row, Input, Form, Upload, notification, AutoComplete, List, Tag, Radio } from 'antd';
import { PaperClipOutlined, UserOutlined, CalendarOutlined, MessageOutlined, RocketOutlined, ProjectOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { Link } from 'react-router-dom';
import DarkDatePicker from './DatePicker';
import AddToProjectModal from './projects/AddToProjectModal';
import { STATUS_META, STATUS_ORDER, getStatusMeta, isResolved as isResolvedStatus } from './statusMeta';

const { Dragger } = Upload;

/* ── status / priority helpers ─────────────────────────────── */
// Status button colours in the admin panel (3 = primary "Vyriešiť")
const STATUS_BUTTON = {
  5: { bg: '#5b4a8a', active: '#c4b5fd' },
  1: { bg: '#1d4f7a', active: '#5abbff' },
  2: { bg: '#4a4af8', active: '#8b8bff' },
  6: { bg: '#0e6b6b', active: STATUS_META[6].color },
  7: { bg: '#13807e', active: STATUS_META[7].color },
  4: { bg: 'darkred', active: '#ff4d4f' },
};

const PRIORITY_META = {
  1: { label: 'URGENT',   bg: '#2d1010', border: 'darkred',  color: '#ff5a5a' },
  2: { label: 'INCIDENT', bg: '#1e1e1e', border: '#594b4b',  color: '#9a9a9a' },
};

const BUG_META = { bg: '#1e1430', border: '#7c4dcc', color: '#b97fff' };

const MetaTag = ({ meta, children }) => (
  <Tag style={{ background: meta?.bg, borderColor: meta?.border, color: meta?.color, fontWeight: 600 }}>
    {children}
  </Tag>
);

/* ── debounce hook ──────────────────────────────────────────── */
const useDebounce = (fn, delay = 700) => {
  const timerRef = useRef({});
  return (key, ...args) => {
    clearTimeout(timerRef.current[key]);
    timerRef.current[key] = setTimeout(() => fn(...args), delay);
  };
};

/* ═══════════════════════════════════════════════════════════════
   INCIDENT MODAL
═══════════════════════════════════════════════════════════════ */
const IncidentModal = ({ open, incident, onClose, fetchIncidents }) => {
  const [form] = Form.useForm();
  const [attachments, setAttachments] = useState([]);
  const [emailOptions, setEmailOptions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);

  /* admin fields */
  const [dueDate, setDueDate] = useState(null);
  const [category, setCategory] = useState(null);
  const [incidentState, setIncidentState] = useState(null);
  const [niceid, setNiceid] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [priority, setPriority] = useState(null);
  const [time, setTime] = useState(null);
  const [plannedEffort, setPlannedEffort] = useState('');
  const [actualEffort, setActualEffort] = useState('');

  /* bug report detail */
  const [isBugReport, setIsBugReport] = useState(false);
  const [appUser, setAppUser] = useState('');
  const [appRole, setAppRole] = useState('');
  const [appPath, setAppPath] = useState('');
  const [expectedResult, setExpectedResult] = useState('');

  /* resolution */
  const [resolution, setResolution] = useState('');
  const [resolutionPrompt, setResolutionPrompt] = useState({ open: false, status: null, text: '' });
  const [addToProjectOpen, setAddToProjectOpen] = useState(false);

  /* auth */
  const [collapsed, setCollapsed] = useState(true);
  const [password, setPassword] = useState('');

  /* ── reference data ─────────────────────────────────── */
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/emails`)
      .then(r => setEmailOptions(r.data))
      .catch(() => {});

    axios.get(`${process.env.REACT_APP_API_URL}/companies`)
      .then(r => setCompanies(r.data))
      .catch(() => {});

    axios.get(`${process.env.REACT_APP_API_URL}/categories`)
      .then(r => setCategories(r.data))
      .catch(() => {});
  }, []);

  /* ── load incident detail ───────────────────────────── */
  useEffect(() => {
    if (!incident) return;
    axios.get(`${process.env.REACT_APP_API_URL}/incident/get/${incident.id}`)
      .then(r => {
        const d = r.data;
        incident.attachments = d.attachments;
        incident.comments    = d.comments;
        setDueDate(d.dueDate ? moment(d.dueDate).toDate() : null);
        setCategory(d.category || null);
        setNiceid(d.niceid || null);
        setSelectedCompany(d.company || null);
        setPriority(d.priority || null);
        setTime(d.time || null);
        setPlannedEffort(d.plannedEffort != null ? String(d.plannedEffort) : '');
        setActualEffort(d.actualEffort  != null ? String(d.actualEffort)  : '');
        setIsBugReport(!!d.isBugReport);
        setAppUser(d.appUser || '');
        setAppRole(d.appRole || '');
        setAppPath(d.appPath || '');
        setExpectedResult(d.expectedResult || '');
        setResolution(d.resolution || '');
        setIncidentState({ ...d });
        form.setFieldsValue({ email: d.email });
      })
      .catch(() => notification.error({ message: 'Chyba pri načítaní detailov incidentu.' }));
  }, [incident, form]);

  useEffect(() => {
    if (password === 'sombrero123') setCollapsed(false);
  }, [password]);

  /* ── paste screenshot ───────────────────────────────── */
  const handlePaste = (e) => {
    const files = [];
    for (const item of e.clipboardData.items) {
      if (item.kind === 'file') files.push(item.getAsFile());
    }
    if (files.length) {
      setAttachments(prev => [
        ...prev,
        ...files.map(f => ({ uid: f.name + Date.now(), name: f.name, status: 'done', originFileObj: f })),
      ]);
      notification.success({ message: 'Screenshot pridaný k prílohám.' });
    }
  };

  useEffect(() => {
    if (open) window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [open]);

  /* ── update incident (immediate) ───────────────────── */
  const updateIncident = (field, value) => {
    // resolution is saved only by its own field – the list row may hold a stale copy
    const { resolution: _staleResolution, ...incidentFields } = incident;
    axios.post(`${process.env.REACT_APP_API_URL}/incident/update`, {
      ...incidentFields,
      [field]: value,
      niceid,
    })
      .then(() => notification.success({ message: `Pole „${field}" bolo aktualizované.` }))
      .catch(() => notification.error({ message: `Chyba pri aktualizácii poľa „${field}".` }));
  };

  /* ── debounced update (text inputs) ─────────────────── */
  const debounced = useDebounce(updateIncident, 700);

  /* ── status change ──────────────────────────────────── */
  const handleStatusChange = (status, resolutionText) => {
    axios.post(`${process.env.REACT_APP_API_URL}/update-status`, {
      incidentId: incident.id,
      status,
      email: incident.email,
      ...(resolutionText !== undefined ? { resolution: resolutionText } : {}),
    })
      .then(() => {
        notification.success({ message: `Status bol zmenený.` });
        onClose();
        if (fetchIncidents) fetchIncidents();
      })
      .catch(() => notification.error({ message: 'Chyba pri aktualizácii statusu.' }));
  };

  // Closing statuses ask "how was it resolved?" first
  const handleStatusClick = (status) => {
    if (isResolvedStatus(status)) {
      setResolutionPrompt({ open: true, status, text: resolution });
    } else {
      handleStatusChange(status);
    }
  };

  const closeResolutionPrompt = () => setResolutionPrompt({ open: false, status: null, text: '' });

  const confirmResolutionPrompt = (withText) => {
    const { status, text } = resolutionPrompt;
    closeResolutionPrompt();
    if (withText) {
      setResolution(text);
      handleStatusChange(status, text);
    } else {
      handleStatusChange(status);
    }
  };

  const handleChangeResolution = (e) => {
    setResolution(e.target.value);
    debounced('resolution', 'resolution', e.target.value);
  };

  /* ── comment submit ─────────────────────────────────── */
  const handleCommentSubmit = (values) => {
    const formData = new FormData();
    formData.append('incidentId', incident.id);
    formData.append('text', values.text);
    formData.append('email', values.email);
    attachments.forEach(f => formData.append('attachments', f.originFileObj));

    axios.post(`${process.env.REACT_APP_API_URL}/add-comment`, formData)
      .then(() => {
        notification.success({ message: 'Komentár bol pridaný.' });
        form.resetFields();
        setAttachments([]);
        onClose();
        if (fetchIncidents) fetchIncidents();
      })
      .catch(() => notification.error({ message: 'Chyba pri pridávaní komentára.' }));
  };

  const handleReclaim = () => {
    form.validateFields()
      .then(values => {
        const formData = new FormData();
        formData.append('incidentId', incident.id);
        formData.append('text', values.text);
        formData.append('email', values.email);
        attachments.forEach(f => formData.append('attachments', f.originFileObj));

        axios.post(`${process.env.REACT_APP_API_URL}/reclaim`, formData)
          .then(() => {
            notification.success({ message: 'Incident bol reklamovaný a vrátený do stavu PRIJATÉ.' });
            form.resetFields();
            setAttachments([]);
            onClose();
            if (fetchIncidents) fetchIncidents();
          })
          .catch(() => notification.error({ message: 'Chyba pri reklamácii incidentu.' }));
      })
      .catch(() => {});
  };

  /* ── admin field handlers ───────────────────────────── */
  const handleChangeDueDate = (date) => {
    setDueDate(date);
    updateIncident('dueDate', moment(date).format('YYYY-MM-DD HH:mm:ss'));
  };

  const handleChangeCategory = (value) => {
    setCategory(value);
    debounced('category', 'category', value);
  };

  const handleChangeCategoryBlur = (e) => {
    setCategory(e.target.value);
    updateIncident('category', e.target.value);
  };

  const handleChangeNiceid = (e) => {
    setNiceid(e.target.value);
    debounced('niceid', 'niceid', e.target.value);
  };

  const handleChangeEmail = (e) => {
    setIncidentState(prev => ({ ...prev, email: e.target.value }));
    debounced('email', 'email', e.target.value);
  };

  const handleChangeCompany = (value) => {
    setSelectedCompany(value);
    debounced('company', 'company', value);
  };

  const handleChangeCompanyBlur = (e) => {
    setSelectedCompany(e.target.value);
    updateIncident('company', e.target.value);
  };

  const handleChangePriority = (e) => {
    setPriority(e.target.value);
    updateIncident('priority', e.target.value);
  };

  const handleChangeTime = (e) => {
    setTime(e.target.value);
    debounced('time', 'time', e.target.value);
  };

  const handleChangePlannedEffort = (e) => {
    setPlannedEffort(e.target.value);
    debounced('plannedEffort', 'plannedEffort', e.target.value);
  };

  const handleChangeActualEffort = (e) => {
    setActualEffort(e.target.value);
    debounced('actualEffort', 'actualEffort', e.target.value);
  };

  /* ── guards ─────────────────────────────────────────── */
  if (!incident) return null;

  const getCompanyName = (alias) => (companies.find(c => c.alias === alias)?.name) || alias;
  const currentStatus = Number(incidentState?.status ?? incident.status);
  const statusMeta   = getStatusMeta(currentStatus);
  const priorityMeta = PRIORITY_META[incident.priority] || PRIORITY_META[2];
  const isResolved   = isResolvedStatus(currentStatus);

  /* ── render ─────────────────────────────────────────── */
  return (
    <Modal
      title={
        <span className="incident-detail-title">
          <span className="incident-detail-title-id">{incident.niceid}</span>
          {incident.text}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={1200}
      className="incident-detail-modal"
      rootClassName="incident-detail-modal-root"
      styles={{ body: { padding: 0, overflow: 'hidden' } }}
      style={{ top: 16, paddingBottom: 0 }}
    >
      {/* ── two-panel layout ── */}
      <div className="incident-detail-layout">

        {/* ══ LEFT: main content ══════════════════════════════ */}
        <div className="incident-detail-main">

          {/* meta tags */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            <MetaTag meta={statusMeta}>{statusMeta.label}</MetaTag>
            {isBugReport ? (
              <MetaTag meta={BUG_META}>ZMENOVÀ POŽIADAVKA / BUG</MetaTag>
            ) : (
              <MetaTag meta={priorityMeta}>{priorityMeta.label}</MetaTag>
            )}
            {isBugReport && priority === 1 && (
              <MetaTag meta={PRIORITY_META[1]}>URGENT</MetaTag>
            )}
            <Tag style={{ background: '#1a1a1a', borderColor: '#333', color: '#aaa' }}>
              <img
                src={`${process.env.REACT_APP_URL}/${incident.company}w.png`}
                alt=""
                style={{ marginRight: 5, verticalAlign: 'middle', height: 14 }}
              />
              <strong>{getCompanyName(incident.company)}</strong>
              <span style={{ color: '#666', marginLeft: 5 }}>({incident.email})</span>
            </Tag>
            <Tag style={{ background: '#1a1a1a', borderColor: '#333', color: '#777' }}>
              Pridané: {new Date(incident.created).toLocaleString()}
            </Tag>
            {incident.lastUpdate && (
              <Tag style={{ background: '#1a1a1a', borderColor: '#333', color: '#777' }}>
                Zmena: {new Date(incident.lastUpdate).toLocaleString()}
              </Tag>
            )}
            {dueDate && (
              <Tag style={{ background: '#1a2215', borderColor: '#3a6029', color: '#7ec86b' }}>
                <CalendarOutlined style={{ marginRight: 4 }} />
                Deadline: {moment(dueDate).format('D. M. YYYY')}
              </Tag>
            )}
            {incidentState?.projectTask ? (
              <Link
                to={`/projects/${incidentState.projectTask.projectId}?task=${incidentState.projectTask.taskId}`}
                onClick={onClose}
                className="incident-project-chip"
              >
                <ProjectOutlined />
                {[incidentState.projectTask.projectName, incidentState.projectTask.phaseTitle, incidentState.projectTask.title]
                  .filter(Boolean).join(' › ')}
              </Link>
            ) : incidentState && (
              <Button size="small" icon={<ProjectOutlined />} onClick={() => setAddToProjectOpen(true)} className="incident-project-add">
                Pridať do projektu
              </Button>
            )}
          </div>

          {/* description */}
          {incident.description && (
            <div
              style={{
                background: '#242527', padding: '12px 14px', borderRadius: 6,
                marginBottom: 20, color: '#aea9a9', lineHeight: '1.7', fontSize: 13,
                border: '1px solid #333',
              }}
              dangerouslySetInnerHTML={{ __html: incident.description }}
            />
          )}

          {/* resolution – editable in the admin panel mode, plain text otherwise */}
          {!collapsed ? (
            <div className="modal-resolution">
              <div className="modal-resolution-title">Riešenie</div>
              <Input.TextArea
                value={resolution}
                onChange={handleChangeResolution}
                autoSize={{ minRows: 2, maxRows: 8 }}
                placeholder="Ako sa požiadavka vyriešila…"
              />
            </div>
          ) : resolution.trim() && (
            <div className="modal-resolution">
              <div className="modal-resolution-title">Riešenie</div>
              <div className="modal-resolution-text">{resolution}</div>
            </div>
          )}

          {/* ── bug report detail ────────────────────── */}
          {isBugReport && (
            <div style={{
              background: '#1e1430', border: '1px solid #7c4dcc', borderRadius: 8,
              padding: '14px 16px', marginBottom: 18,
            }}>
              <div style={{ color: '#b97fff', fontWeight: 700, fontSize: 12, letterSpacing: 1, marginBottom: 10 }}>
                DETAIL ZMENOVEJ POŽIADAVKY / BUGU
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <tbody>
                  {appUser && (
                    <tr>
                      <td style={{ color: '#8e7aaa', padding: '3px 0', width: 180, verticalAlign: 'top' }}>Prihlásený užívateľ</td>
                      <td style={{ color: '#ddd', padding: '3px 0' }}>{appUser}</td>
                    </tr>
                  )}
                  {appRole && (
                    <tr>
                      <td style={{ color: '#8e7aaa', padding: '3px 0', verticalAlign: 'top' }}>Použitá rola</td>
                      <td style={{ color: '#ddd', padding: '3px 0' }}>{appRole}</td>
                    </tr>
                  )}
                  {appPath && (
                    <tr>
                      <td style={{ color: '#8e7aaa', padding: '3px 0', verticalAlign: 'top' }}>Cesta v aplikácii</td>
                      <td style={{ color: '#ddd', padding: '3px 0', fontFamily: 'monospace', fontSize: 12 }}>{appPath}</td>
                    </tr>
                  )}
                  {expectedResult && (
                    <tr>
                      <td style={{ color: '#8e7aaa', padding: '3px 0', verticalAlign: 'top' }}>Očakávaný výsledok</td>
                      <td style={{ color: '#ddd', padding: '3px 0', lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ __html: expectedResult.replace(/\n/g, '<br/>') }} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── admin panel ──────────────────────────── */}
          <div style={{ marginTop: 12 }}>
            {collapsed ? (
              <Input.Password
                placeholder="prístupový kód k správe"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={{ maxWidth: 280 }}
              />
            ) : (
              <div className="modal-admin-panel">
                <div className="modal-admin-title">Správca incidentu</div>
                <Form layout="horizontal" colon={false} labelCol={{ style: { minWidth: 160, color: '#888', fontWeight: 500, fontSize: 12 } }}>

                  <Form.Item label="Termín splnenia">
                    <div className="modal-admin-datepicker-wrap">
                      <CalendarOutlined style={{ color: '#555', marginRight: 8 }} />
                      <DarkDatePicker
                        selected={dueDate}
                        onChange={handleChangeDueDate}
                        showTimeSelect
                        dateFormat="dd.MM.yyyy HH:mm"
                      />
                    </div>
                  </Form.Item>

                  <Form.Item label="Kategória">
                    <AutoComplete
                      options={categories.map(c => ({ value: c }))}
                      onSelect={v => { setCategory(v); updateIncident('category', v); }}
                      onChange={handleChangeCategory}
                      onBlur={handleChangeCategoryBlur}
                      value={category}
                      filterOption={(input, option) =>
                        String(option?.value || '').toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      <Input placeholder="Kategória..." />
                    </AutoComplete>
                  </Form.Item>

                  <Form.Item label="E-mail">
                    <Input
                      value={incidentState?.email || ''}
                      onChange={handleChangeEmail}
                      placeholder="E-mail..."
                    />
                  </Form.Item>

                  <Form.Item label="ID incidentu">
                    <Input value={niceid} onChange={handleChangeNiceid} />
                  </Form.Item>

                  <Form.Item label="Spoločnosť">
                    <AutoComplete
                      options={companies.map(c => ({ value: c.alias, label: `${c.alias} – ${c.name}` }))}
                      onSelect={v => { setSelectedCompany(v); updateIncident('company', v); }}
                      onChange={handleChangeCompany}
                      onBlur={handleChangeCompanyBlur}
                      value={selectedCompany}
                      filterOption={(input, option) =>
                        String(option?.label || option?.value || '').toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      <Input placeholder="Alias spoločnosti..." />
                    </AutoComplete>
                  </Form.Item>

                  <Form.Item label="Priorita">
                    <Radio.Group onChange={handleChangePriority} value={priority}>
                      <Radio value={2}>NORMAL</Radio>
                      <Radio value={1}>URGENT</Radio>
                    </Radio.Group>
                  </Form.Item>

                  <Form.Item label="Čas (minúty)">
                    <Input type="number" value={time} onChange={handleChangeTime} style={{ maxWidth: 120 }} />
                  </Form.Item>

                  <Form.Item label="Náročnosť plánovaná (MD)">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={plannedEffort}
                      onChange={handleChangePlannedEffort}
                      placeholder="napr. 1.5"
                      style={{ maxWidth: 120 }}
                    />
                  </Form.Item>

                  <Form.Item label="Náročnosť reálna (MD)">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      value={actualEffort}
                      onChange={handleChangeActualEffort}
                      placeholder="napr. 2"
                      style={{ maxWidth: 120 }}
                    />
                  </Form.Item>

                  <Form.Item label="Stav">
                    <div className="modal-status-actions">
                      {STATUS_ORDER.map((code) => {
                        const active = currentStatus === code;
                        const btn = STATUS_BUTTON[code];
                        return (
                          <Button
                            key={code}
                            onClick={() => handleStatusClick(code)}
                            type={btn ? 'default' : 'primary'}
                            icon={code === 7 ? <RocketOutlined /> : undefined}
                            className={active ? 'modal-status-btn is-active' : 'modal-status-btn'}
                            style={btn
                              ? { '--status-btn-bg': btn.bg, background: btn.bg, color: 'white', border: active ? `2px solid ${btn.active}` : 'none' }
                              : undefined}
                          >
                            {STATUS_META[code].short}
                          </Button>
                        );
                      })}
                    </div>
                  </Form.Item>
                </Form>
              </div>
            )}
          </div>
        </div>

        {/* ══ RIGHT: sidebar ══════════════════════════════════ */}
        <div className="modal-sidebar">

          {/* attachments */}
          {incident.attachments && incident.attachments.length > 0 && (
            <div className="modal-sidebar-section">
              <div className="modal-sidebar-heading">
                <PaperClipOutlined style={{ marginRight: 6 }} />
                Prílohy ({incident.attachments.length})
              </div>
              <div className="modal-sidebar-list">
                {incident.attachments.map(att => (
                  <a
                    key={att.id || att.filename}
                    className="modal-att-link"
                    href={`${process.env.REACT_APP_API_URL}/uploads/${att.filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <PaperClipOutlined style={{ marginRight: 5 }} />
                    {att.originalFilename}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* comments list */}
          <div className="modal-sidebar-section" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div className="modal-sidebar-heading">
              <MessageOutlined style={{ marginRight: 6 }} />
              Komentáre ({(incident.comments || []).length})
            </div>
            <div className="modal-comments-list">
              {!incident.comments || incident.comments.length === 0 ? (
                <div className="modal-sidebar-empty">Žiadne komentáre</div>
              ) : (
                incident.comments.map(c => (
                  <div key={c.id} className="modal-comment-item">
                    <div className="modal-comment-header">
                      <a href={`mailto:${c.email}`} className="modal-comment-email">{c.email}</a>
                      <span className="modal-comment-date">{moment(c.datetime).format('D.M.YYYY HH:mm')}</span>
                    </div>
                    <div
                      className="modal-comment-text"
                      dangerouslySetInnerHTML={{ __html: c.text }}
                    />
                    {c.attachments && c.attachments.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        {c.attachments.map((a, i) => (
                          <a
                            key={i}
                            href={`${process.env.REACT_APP_API_URL}/uploads/${a.filename}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="modal-att-link"
                            style={{ display: 'inline-flex', marginRight: 8 }}
                          >
                            <PaperClipOutlined style={{ marginRight: 4 }} />
                            {a.originalFilename}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* new comment form */}
          <div className="modal-sidebar-section modal-new-comment">
            <div className="modal-sidebar-heading">Nový komentár</div>
            <Form form={form} onFinish={handleCommentSubmit} layout="vertical">
              <Form.Item name="email" initialValue={incident.email} rules={[{ required: true, message: 'Vložte e-mail.' }]} style={{ marginBottom: 8 }}>
                <AutoComplete
                  options={emailOptions.map(e => ({ value: e }))}
                  filterOption={(input, option) =>
                    String(option?.value || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  <Input
                    placeholder="Váš e-mail..."
                    prefix={<UserOutlined style={{ marginRight: 4 }} />}
                    size="small"
                  />
                </AutoComplete>
              </Form.Item>
              <Form.Item name="text" rules={[{ required: true, message: 'Vložte komentár.' }]} style={{ marginBottom: 8 }}>
                <Input.TextArea rows={3} placeholder="Komentár..." />
              </Form.Item>
              <Form.Item style={{ marginBottom: 6 }}>
                <Dragger
                  fileList={attachments}
                  beforeUpload={() => false}
                  onChange={({ fileList }) => setAttachments(fileList)}
                  onRemove={f => setAttachments(prev => prev.filter(x => x.uid !== f.uid))}
                  multiple
                  style={{ padding: '4px 0' }}
                >
                  <p className="ant-upload-text" style={{ fontSize: 12 }}>Kliknite alebo vložte súbor</p>
                </Dragger>
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button type="primary" htmlType="submit" style={{ flex: 1 }}>
                    Odoslať komentár
                  </Button>
                  {isResolved && (
                    <Button
                      onClick={handleReclaim}
                      style={{ flex: 1, fontWeight: 700, background: '#cf1322', color: '#fff', border: 'none' }}
                    >
                      Reklamovať
                    </Button>
                  )}
                </div>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>

      <Modal
        open={resolutionPrompt.open}
        title="Ako sa to vyriešilo?"
        onCancel={closeResolutionPrompt}
        centered
        footer={[
          <Button key="cancel" onClick={closeResolutionPrompt}>Zrušiť</Button>,
          <Button key="skip" onClick={() => confirmResolutionPrompt(false)}>Preskočiť</Button>,
          <Button key="save" type="primary" onClick={() => confirmResolutionPrompt(true)}>Uložiť a uzavrieť</Button>,
        ]}
      >
        <Input.TextArea
          value={resolutionPrompt.text}
          onChange={(e) => setResolutionPrompt(prev => ({ ...prev, text: e.target.value }))}
          autoSize={{ minRows: 3, maxRows: 10 }}
          placeholder="Napr. opravená konfigurácia, nasadená verzia 2.3…"
          autoFocus
        />
      </Modal>

      {addToProjectOpen && (
        <AddToProjectModal
          open={addToProjectOpen}
          incident={incident}
          onClose={() => setAddToProjectOpen(false)}
          onAdded={() => {
            setAddToProjectOpen(false);
            axios.get(`${process.env.REACT_APP_API_URL}/incident/get/${incident.id}`)
              .then(r => setIncidentState({ ...r.data }))
              .catch(() => {});
          }}
        />
      )}
    </Modal>
  );
};

export default IncidentModal;
